import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import MainGrid from '../../components/MainGrid';
import {
	Add,
	AutoAwesomeOutlined,
	Calculate,
	Download,
	DriveFolderUpload,
	MedicalServices,
	Person,
} from '@mui/icons-material';
import { useState } from 'react';
import Dropzone from 'react-dropzone';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { CircleClose } from '../../assets/icons/Icons';
import { helperText } from '../../assets/theme/Theme';
import {
	fetchAdmissionAssessmentData,
	generateAdmissionAssessment,
} from '../../services/CarePlanServices';
import { toast } from 'react-toastify';
import { getElderHomeId, getUserId } from '../../Utils/AuthUtil';
import { v4 as uuidv4 } from 'uuid';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { dark, teal } from '../../assets/theme/Colors';

const INITIAL_ERROR_STATE = {
	status: false,
	msg: '',
};

const initialData = {
	name: '',
	nhs_number: '',
	date_of_birth: '',
	date_of_assessment: new Date().toISOString().split('T')[0],
	place_of_assessment: '',
	contact_number: '',
};

const assessmentFields = [
	{ key: 'breathing', label: 'Breathing' },
	{ key: 'choking_risk', label: 'Choking Risk' },
	{
		key: 'cognitive_ability_behaviour',
		label: 'Cognitive Ability & Behavior',
	},
	{ key: 'communication', label: 'Communication' },
	{ key: 'current_medical_condition', label: 'Current Medical Condition' },
	{ key: 'eating_drinking', label: 'Eating & Drinking' },
	{ key: 'expressing_sexuality', label: 'Expressing Sexuality' },
	{ key: 'glasses', label: 'Glasses' },
	{ key: 'hearing_aids', label: 'Hearing Aids' },
	{ key: 'immediate_health_needs', label: 'Immediate Health Needs' },
	{ key: 'interests_hobbies', label: 'Interests & Hobbies' },
	{ key: 'known_allergies', label: 'Known Allergies' },
	{ key: 'list_of_medication', label: 'List Of Medication' },
	{ key: 'mobility', label: 'Mobility' },
	{ key: 'other_notes', label: 'Other Notes' },
	{ key: 'pain', label: 'Pain' },
	{ key: 'past_medical_history', label: 'Past Medical History' },
	{ key: 'personal_hygiene_needs', label: 'Personal Hygiene Needs' },
	{ key: 'pressure_areas', label: 'Pressure Areas' },
	{ key: 'sleeping_resting', label: 'Sleeping & Resting' },
	{
		key: 'specialised_medication_routines',
		label: 'Specialized Medication Routines',
	},
	{
		key: 'specialist_equipment_needed',
		label: 'Specialist Equipment Needed',
	},
];

const SCORE_ORDER = ['Low', 'Medium', 'High'];

const SCORE_DATA = [
	{ name: 'Low', label: '0-5 pts · 14 hrs/wk' },
	{ name: 'Medium', label: '6-10 pts · 14 hrs/wk' },
	{ name: 'High', label: '11-15 pts · 14 hrs/wk' },
];

const steps = ['Upload Documents', 'Review Details', 'Assessment & Cost'];

const formatLabel = (str = '') =>
	str
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

const LabeledTextField = ({
	label,
	placeholder,
	value,
	onChange,
	required = false,
	name,
	type = 'text',
	multiline,
	rows,
	error,
}) => {
	return (
		<Box>
			<Typography
				sx={{
					fontSize: 12,
					fontWeight: 600,
					color: '#6b8888',
					mb: 0.7,
				}}>
				{label}
			</Typography>

			<TextField
				type={type}
				name={name}
				placeholder={placeholder}
				value={value}
				onChange={e => onChange(e.target.value)}
				required={required}
				fullWidth
				size="small"
				sx={{
					'& .MuiInputBase-input': {
						fontSize: 13,
						'&::placeholder': {
							fontSize: 13,
						},
					},
				}}
				multiline={multiline}
				rows={rows}
				error={error?.status}
			/>
			{error?.status && (
				<Typography style={helperText}>{error?.msg}</Typography>
			)}
		</Box>
	);
};

function HtmlBlock({ html }) {
	return (
		<Box
			sx={{
				fontSize: 12,
				color: '#4b5563',
				lineHeight: 1.7,
				'& p': { margin: '0 0 8px 0' },
				'& ul': { paddingLeft: '18px', margin: 0 },
				'& li': { marginBottom: '6px' },
			}}
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	);
}

function AdmissionStep1Upload({
	data,
	updateField,
	handleSubmit,
	fileData,
	readTextFile,
	onRemoveFiles,
	isLoading,
	errors,
	txtError,
}) {
	const formatDateInput = value => {
		const digits = value.replace(/\D/g, '');

		if (digits.length <= 4) return digits;
		if (digits.length <= 6)
			return `${digits.slice(0, 4)}-${digits.slice(4)}`;
		return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
	};

	return (
		<Grid container spacing={3}>
			<Grid item xs={12} md={6}>
				<Card
					sx={{
						border: '1px solid #d8e8e8',
						borderRadius: '12px',
					}}>
					<CardContent sx={{ p: 3 }}>
						<Grid
							container
							flexDirection={'row'}
							alignItems={'center'}>
							<Person sx={{ color: '#00b7a8' }} />
							<Typography
								fontWeight={600}
								color="#102c2c"
								marginLeft={'5px'}
								fontSize={13}>
								Residents Without Care Plans
							</Typography>
						</Grid>
						<Stack spacing={2} marginTop={2}>
							<LabeledTextField
								label="Full Name"
								value={data.name}
								onChange={val => updateField('name', val)}
								required={true}
								placeholder="e.g. Patricia Hammond"
								error={errors.name}
							/>

							<LabeledTextField
								label="NHS Number"
								placeholder="e.g. 485 777 3456"
								value={data.nhs_number}
								onChange={val => updateField('nhs_number', val)}
								required={true}
								error={errors.nhs_number}
							/>

							<LabeledTextField
								type={'date'}
								label="Date of birth"
								value={data.date_of_birth}
								onChange={val =>
									updateField(
										'date_of_birth',
										formatDateInput(val),
									)
								}
								required={true}
								error={errors.date_of_birth}
							/>

							<LabeledTextField
								label="Place of assessment"
								placeholder="e.g. elder home"
								value={data.nhsNumber}
								onChange={val =>
									updateField('place_of_assessment', val)
								}
								required={true}
								error={errors.place_of_assessment}
							/>

							<LabeledTextField
								type={'date'}
								label="Date of assessment"
								value={data.date_of_assessment}
								onChange={val =>
									updateField(
										'date_of_assessment',
										formatDateInput(val),
									)
								}
								required={true}
								error={errors.date_of_assessment}
							/>

							<LabeledTextField
								label="Contact number"
								placeholder="e.g. 01234567890"
								value={data.contact_number}
								onChange={val =>
									updateField('contact_number', val)
								}
								required={true}
								error={errors.contact_number}
							/>
						</Stack>
					</CardContent>
				</Card>
			</Grid>
			<Grid item xs={12} md={6}>
				<Card
					sx={{
						border: '1px solid #d8e8e8',
						borderRadius: '12px',
					}}>
					<CardContent sx={{ p: 3 }}>
						<Grid
							container
							flexDirection={'row'}
							alignItems={'center'}>
							<DriveFolderUpload sx={{ color: '#00b7a8' }} />
							<Typography
								fontWeight={600}
								color="#102c2c"
								marginLeft={'5px'}
								fontSize={13}>
								Upload Referral Documents
							</Typography>
						</Grid>
						<Stack spacing={2} marginTop={2}>
							<Dropzone
								onDrop={acceptedFiles =>
									readTextFile(acceptedFiles)
								}>
								{({ getRootProps, getInputProps }) => (
									<Paper
										{...getRootProps()}
										elevation={0}
										sx={{
											borderRadius: 3,

											background: '#eefcf9',
											py: 4,
											textAlign: 'center',
											cursor: 'pointer',
											border: txtError.status
												? '1px solid red'
												: '1px solid #a5e8e1',
										}}>
										<input {...getInputProps()} />
										<UploadFileIcon
											sx={{
												fontSize: 40,
												color: '#00b7a8',
												mb: 1,
											}}
										/>
										<>
											<Typography
												fontWeight={700}
												fontSize={14}
												color={dark}>
												Drop files here or click to
												upload
											</Typography>

											<Typography
												variant="body2"
												sx={{
													color: '#6b8888',
													mt: 0.5,
													fontSize: 12,
												}}>
												GP letters · hospital discharge
												summaries · social worker
												assessments · OT reports
											</Typography>
										</>
									</Paper>
								)}
							</Dropzone>
							{fileData && fileData.length > 0 && (
								<Grid container alignItems="center">
									{fileData.map((file, index) => {
										return (
											<Grid
												key={index}
												container
												alignItems="center"
												justifyContent="space-between"
												wrap="nowrap"
												sx={{
													border: '1px solid #b3ede8',
													borderRadius: '8px',
													padding: '6px 10px',
													marginBottom: '5px',
													backgroundColor: '#e6f9f7',
												}}>
												<Grid
													item
													xs
													sx={{
														overflow: 'hidden',
													}}>
													<Typography
														fontSize="12px"
														color={'#1a2424'}
														fontWeight={500}
														title={file?.name}>
														{file?.name}
													</Typography>
												</Grid>
												<Grid item>
													<IconButton
														disableRipple
														style={{
															padding: 0,
															marginLeft: '6px',
														}}
														disabled={isLoading}
														onClick={() =>
															onRemoveFiles(file)
														}>
														<CircleClose />
													</IconButton>
												</Grid>
											</Grid>
										);
									})}
								</Grid>
							)}
							{txtError.status && (
								<Typography style={helperText}>
									{txtError.msg}
								</Typography>
							)}
						</Stack>
					</CardContent>
				</Card>
				<Card
					sx={{
						border: '1px solid #d8e8e8',
						borderRadius: '12px',
						backgroundColor: dark,
						marginTop: '10px',
					}}>
					<CardContent sx={{ p: 3 }}>
						<Grid
							container
							flexDirection={'row'}
							alignItems={'center'}>
							<AutoAwesomeOutlined sx={{ color: '#00b7a8' }} />
							<Typography
								fontWeight={600}
								color="#fff"
								marginLeft={'5px'}
								fontSize={13}>
								AI will extract from your documents
							</Typography>
						</Grid>
						<Stack spacing={2} marginTop={2}>
							<Typography
								fontSize={'13px'}
								color="#ffffff8c"
								mt={0.5}>
								Conditions · medications · allergies · mobility
								· continence · communication · risk flags · care
								preferences — all extracted automatically for
								your review.
							</Typography>
							<Button
								onClick={handleSubmit}
								startIcon={
									<AutoAwesomeOutlined
										sx={{ width: 16, height: 16 }}
									/>
								}
								sx={{
									backgroundColor: teal,
									color: dark,
									fontSize: '12px',
									fontWeight: 600,
									padding: '8px 16px',
									borderRadius: '8px',
									textTransform: 'none',
									'&:hover': {
										backgroundColor: '#00b0a0',
									},
								}}>
								Extract & Continue
							</Button>
						</Stack>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
}

function AdmissionStep2Review({ data, updateField, onGenerate }) {
	return (
		<Grid container spacing={3}>
			<Grid item xs={12} md={12}>
				<Card
					sx={{
						border: '1px solid #d8e8e8',
						borderRadius: '12px',
						marginTop: '10px',
					}}>
					<CardContent sx={{ p: 3 }}>
						<Grid
							container
							flexDirection={'row'}
							alignItems={'center'}>
							<MedicalServices sx={{ color: '#00b7a8' }} />
							<Typography
								fontWeight={600}
								color="#102c2c"
								marginLeft={'5px'}
								fontSize={13}>
								Clinical Details
							</Typography>
						</Grid>

						<Stack spacing={2} marginTop={2}>
							{Object.keys(data).map((field, index) => (
								<LabeledTextField
									key={index}
									label={formatLabel(field)}
									value={data[field] ?? ''}
									onChange={val => updateField(field, val)}
									multiline
									rows={5}
								/>
							))}

							<Stack direction="row" spacing={2}>
								<Button
									onClick={onGenerate}
									startIcon={
										<Calculate
											sx={{ width: 16, height: 16 }}
										/>
									}
									sx={{
										backgroundColor: teal,
										color: dark,
										fontSize: '12px',
										fontWeight: 600,
										padding: '8px 16px',
										borderRadius: '8px',
										textTransform: 'none',
										'&:hover': {
											backgroundColor: '#00b0a0',
										},
									}}>
									Generate Assessment & Cost Estimate
								</Button>
							</Stack>
						</Stack>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
}

function AdmissionStep3Assessment({ data, onDownload, onClickNew }) {
	const CARE_TYPES = [
		'Residential',
		'Residential Dementia',
		'Nursing',
		'Nursing Dementia',
	].map(key => ({
		key,
		label: key,
		rate: data.config_data.care_type_base_rates[key],
	}));

	const SCORE_LABEL = ['Independent', 'Moderate', 'High'];

	const fmt = n => '£' + Number(n).toLocaleString('en-GB');
	const cost = data.cost;
	const careRow = CARE_TYPES.find(x => x.key === cost.care_type);
	const score = data.dependency_score ?? SCORE_DATA;

	return (
		<Box
			sx={{
				background: '#f1f5f9',
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
			}}>
			{/* ───── INVOICE ───── */}
			<Card
				sx={{
					borderRadius: 2,
					overflow: 'hidden',
				}}>
				<CardContent>
					{/* Header */}
					<Box
						sx={{
							p: 3,
							display: 'flex',
							justifyContent: 'space-between',
							borderBottom: '1px solid #f1f5f9',
						}}>
						<Box>
							<Typography
								sx={{
									fontSize: 16,
									fontWeight: 600,
									color: '#111827',
								}}>
								Pre-Admission Cost Estimate
							</Typography>

							<Typography sx={{ fontSize: 12, color: '#6b7280' }}>
								{data.resident} · Ref:{' '}
								{data.assessment_id &&
									data?.assessment_id
										.slice(0, 8)
										.toUpperCase()}{' '}
								· {data.assessed_date}
							</Typography>
						</Box>

						<Chip
							label={cost.care_type}
							sx={{
								background: '#0f766e',
								color: '#fff',
								fontSize: 12,
							}}
						/>
					</Box>

					{/* Weekly */}
					<Box sx={{ px: 3, py: 2 }}>
						<Typography
							sx={{
								fontSize: 10,
								fontWeight: 700,
								color: '#94a3b8',
								textTransform: 'uppercase',
								mb: 1,
							}}>
							Estimated Weekly Fee
						</Typography>

						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								py: 1,
								borderBottom: '1px solid #f8fafc',
							}}>
							<Typography sx={{ fontSize: 13, color: '#374151' }}>
								{careRow.label} — Base Weekly Rate
							</Typography>
							<Typography sx={{ fontWeight: 600 }}>
								{fmt(cost.base_weekly_rate_config)}
							</Typography>
						</Box>

						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
							}}>
							<Typography sx={{ fontSize: 13, color: '#374151' }}>
								<Box
									component="span"
									sx={{ color: '#374151', fontSize: 13 }}>
									Care Supplement
								</Box>{' '}
								<Box
									component="span"
									sx={{ color: '#94a3b8', fontSize: 11 }}>
									{cost.care_hours_per_week_config} hrs x £
									{cost.hourly_care_rate_config}/hr ·{' '}
									{cost.dependency_band}
								</Box>
							</Typography>
							<Typography sx={{ fontWeight: 600 }}>
								{fmt(cost.care_element)}
							</Typography>
						</Box>
					</Box>

					{/* Total */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							px: 3,
							py: 2,
							background: '#f8fafc',
						}}>
						<Typography>Estimated Weekly Total</Typography>
						<Typography
							sx={{
								fontSize: 18,
								fontWeight: 700,
								color: '#0f766e',
							}}>
							{fmt(cost.estimated_weekly_total)}
						</Typography>
					</Box>

					{/* One time */}
					<Box
						sx={{
							px: 3,
							py: 2,
							display: 'flex',
							justifyContent: 'space-between',
						}}>
						<Box>
							<Typography
								sx={{
									fontSize: 13,
									color: '#374151',
								}}>
								<Box
									component="span"
									sx={{
										color: '#374151',
										fontSize: 13,
										fontWeight: 600,
									}}>
									Equipment Cost
								</Box>{' '}
								<Box
									component="span"
									sx={{ color: '#94a3b8', fontSize: 11 }}>
									({cost.equipment.length} items - see
									Equipment Schedule below)
								</Box>
							</Typography>
							<Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
								One-time · Payable on admission · Not included
								in weekly fee
							</Typography>
						</Box>

						<Typography sx={{ fontSize: 18, fontWeight: 800 }}>
							{fmt(cost.one_time_total)}
						</Typography>
					</Box>
				</CardContent>
			</Card>
			{/* ───── TWO COLUMN ───── */}
			<Grid container spacing={2}>
				{/* LEFT */}
				<Grid item xs={12} md={6}>
					<Card sx={{ borderRadius: 2 }}>
						<CardContent>
							<Box
								sx={{
									borderBottom: '1px solid #f1f5f9',
									marginBottom: 2,
									pb: 1,
								}}>
								<Typography
									sx={{
										fontSize: 11,
										fontWeight: 700,
										color: '#475569',
										textTransform: 'uppercase',
									}}>
									Assessment Rationale
								</Typography>
							</Box>
							<Typography
								sx={{
									fontSize: 10,
									fontWeight: 700,
									color: '#94a3b8',
									textTransform: 'uppercase',
									mb: 1,
								}}>
								Care Type
							</Typography>

							<Table size="small" sx={{ marginBottom: '15px' }}>
								<TableBody>
									{CARE_TYPES.map(ct => (
										<TableRow
											key={ct.key}
											sx={
												ct.key === cost.care_type
													? { background: '#f0fdfa' }
													: {}
											}>
											<TableCell
												sx={{
													fontSize: 13,
													fontWeight: 500,
													color: '#94a3b8',
												}}>
												{ct.label}
												{ct.key === cost.care_type && (
													<Chip
														label="AI Assisted"
														size="small"
														sx={{
															ml: 1,
															fontSize: 9,
															height: 18,
															background:
																'#0f766e',
															color: '#fff',
														}}
													/>
												)}
											</TableCell>

											<TableCell
												align="right"
												sx={{
													fontWeight: 600,
													color: '#94a3b8',
												}}>
												{fmt(ct.rate)} /wk
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>

							{/* Care Type Reason */}
							<HtmlBlock html={cost.care_type_reason} />
						</CardContent>
					</Card>
				</Grid>

				{/* RIGHT */}
				<Grid item xs={12} md={6}>
					<Card sx={{ borderRadius: 2 }}>
						<CardContent>
							<Box
								sx={{
									borderBottom: '1px solid #f1f5f9',
									marginBottom: 2,
									pb: 1,
									justifyContent: 'space-between',
									flexDirection: 'row',
									display: 'flex',
								}}>
								<Box>
									<Typography
										sx={{
											fontSize: 11,
											fontWeight: 700,
											color: '#475569',
											textTransform: 'uppercase',
										}}>
										Dependency Profile
									</Typography>
									<Typography
										sx={{
											fontSize: 10,
											fontWeight: 500,
											color: '#475569',
										}}>
										(Based on functional/personal care
										needs)
									</Typography>
								</Box>
								<Chip
									label={cost.dependency_band}
									sx={{
										background: '#fef3c7',
										fontWeight: 700,
										color: '#92400e',
									}}
								/>
							</Box>
							<Box
								sx={{
									display: 'flex',
									mb: 2,
									justifyContent: 'space-between',
								}}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
									}}>
									<Box
										sx={{
											borderBottom: '1px solid #f1f5f9',
											marginBottom: 2,
											pb: 1,
											ml: 2,
										}}>
										<Typography
											sx={{
												fontSize: 32,
												fontWeight: 900,
											}}>
											{cost.dependency_score}
											<span
												style={{
													fontSize: 14,
													fontWeight: 400,
												}}>
												/16
											</span>
										</Typography>
										<Typography
											sx={{
												fontSize: 11,
												fontWeight: 700,
												color: '#94a3b8',
											}}>
											Total Score
										</Typography>
									</Box>

									<Box
										sx={{
											borderBottom: '1px solid #f1f5f9',
											pb: 1,
											ml: 2,
										}}>
										<Typography
											sx={{
												fontSize: 11,
												fontWeight: 700,
												color: '#94a3b8',
												textTransform: 'uppercase',
											}}>
											BAND
										</Typography>
										<Chip
											label={cost.dependency_band}
											sx={{
												background: '#fef3c7',
												fontWeight: 700,
												color: '#92400e',
											}}
										/>
									</Box>
								</Box>
								<Box
									sx={{
										borderBottom: '1px solid #f1f5f9',
										marginBottom: 2,
										pb: 1,
										ml: 2,
									}}>
									<Typography
										sx={{ fontSize: 32, fontWeight: 900 }}>
										{cost.care_hours_per_week_config} hrs
									</Typography>
									<Typography
										sx={{
											fontSize: 11,
											fontWeight: 700,
											color: '#94a3b8',
										}}>
										care / week
									</Typography>
								</Box>
							</Box>
							{/* Band bar */}
							<Box
								sx={{
									display: 'flex',
									border: '1px solid #e2e8f0',
									borderRadius: 1,
									overflow: 'hidden',
									mb: 2,
									backgroundColor: '#f8fafc',
								}}>
								{score.map(sc => (
									<Box
										key={sc.name}
										sx={{
											flex: 1,
											textAlign: 'center',
											p: 1,
											backgroundColor:
												cost.dependency_band === sc.name
													? '#fffbeb'
													: '#f8fafc',
											borderRight: '1px solid #e2e8f0',
										}}>
										<Typography
											sx={{
												fontSize: 10,
												fontWeight: 700,
												textTransform: 'uppercase',
												color:
													cost.dependency_band ===
													sc.name
														? '#92400e;'
														: '#94a3b8',
											}}>
											{sc.name}
										</Typography>
										<Typography
											sx={{
												fontSize: 10,
												color: '#94a3b8',
											}}>
											{sc.label}
										</Typography>
									</Box>
								))}
							</Box>
							<Box
								sx={{
									display: 'flex',
									gap: 1.5,
									alignItems: 'center',
									px: 2.5,
									py: 1,
									fontSize: 11,
									color: '#6b7280',
									borderBottom: '1px solid #f1f5f9',
									bgcolor: '#f8fafc',
								}}>
								{/* Label */}
								<Typography
									sx={{
										fontSize: 10,
										fontWeight: 700,
										textTransform: 'uppercase',
										letterSpacing: '0.06em',
										color: '#94a3b8',
										flexShrink: 0,
									}}>
									Score
								</Typography>

								{/* Item 0 */}
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
									}}>
									<Box
										sx={{
											width: 18,
											height: 18,
											borderRadius: '3px',
											fontSize: 10,
											fontWeight: 700,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
											bgcolor: '#f1f5f9',
											color: '#64748b',
										}}>
										0
									</Box>
									Independent
								</Box>

								{/* Item 1 */}
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
									}}>
									<Box
										sx={{
											width: 18,
											height: 18,
											borderRadius: '3px',
											fontSize: 10,
											fontWeight: 700,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											bgcolor: '#fef3c7',
											color: '#92400e',
										}}>
										1
									</Box>
									Moderate
								</Box>

								{/* Item 2 */}
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
									}}>
									<Box
										sx={{
											width: 18,
											height: 18,
											borderRadius: '3px',
											fontSize: 10,
											fontWeight: 700,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											bgcolor: '#fee2e2',
											color: '#991b1b',
										}}>
										2
									</Box>
									High
								</Box>
							</Box>
							<TableContainer>
								<Table
									size="small"
									sx={{
										tableLayout: 'fixed',
										'& th, & td': {
											borderBottom: '1px solid #f8fafc',
											verticalAlign: 'top',
										},
									}}>
									<TableHead>
										<TableRow>
											<TableCell
												sx={{
													width: 130,
													fontSize: 10,
													fontWeight: 700,
													color: '#94a3b8',
													textTransform: 'uppercase',
												}}>
												Domain
											</TableCell>

											<TableCell
												sx={{
													width: 120,
													fontSize: 10,
													fontWeight: 700,
													color: '#94a3b8',
													textTransform: 'uppercase',
												}}>
												Score
											</TableCell>

											<TableCell
												sx={{
													fontSize: 10,
													fontWeight: 700,
													color: '#94a3b8',
													textTransform: 'uppercase',
												}}>
												Note
											</TableCell>
										</TableRow>
									</TableHead>

									<TableBody>
										{Object.keys(cost.score_breakdown).map(
											k => {
												const s =
													cost.score_breakdown[k];

												return (
													<TableRow key={k}>
														<TableCell>
															<Typography
																sx={{
																	fontSize: 12,
																	fontWeight: 600,
																}}>
																{formatLabel(k)}
															</Typography>
														</TableCell>

														<TableCell>
															<Box
																sx={{
																	display:
																		'flex',
																	alignItems:
																		'center',
																	gap: 1,
																	flexWrap:
																		'nowrap',
																}}>
																<Box
																	sx={{
																		width: 20,
																		height: 20,
																		flexShrink: 0,
																		display:
																			'flex',
																		alignItems:
																			'center',
																		justifyContent:
																			'center',
																		borderRadius: 1,
																		fontSize: 10,
																		fontWeight: 700,
																		background:
																			s ===
																			0
																				? '#f1f5f9'
																				: s ===
																					  1
																					? '#fef3c7'
																					: '#fee2e2',
																		color:
																			s ===
																			0
																				? '#64748b'
																				: s ===
																					  1
																					? '#92400e'
																					: '#991b1b',
																	}}>
																	{s}
																</Box>

																<Typography
																	sx={{
																		fontSize: 11,
																		whiteSpace:
																			'nowrap',
																	}}>
																	{
																		SCORE_LABEL[
																			s
																		]
																	}
																</Typography>
															</Box>
														</TableCell>

														<TableCell>
															<HtmlBlock
																html={
																	data.cost
																		.score_breakdown_reasons_text[
																		k
																	]
																}
															/>
														</TableCell>
													</TableRow>
												);
											},
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* ───── EQUIPMENT ───── */}
			<Card sx={{ borderRadius: 2 }}>
				<CardContent>
					<Box
						width={'100%'}
						justifyContent="space-between"
						display={'flex'}
						flexDirection={'row'}>
						<Typography
							sx={{
								fontSize: 11,
								fontWeight: 700,
								color: '#475569',
								textTransform: 'uppercase',
								mb: 2,
							}}>
							Equipment Schedule
						</Typography>
						<Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
							One-time · Payable on admission
						</Typography>
					</Box>

					<TableContainer>
						<Table>
							<TableHead>
								<TableRow sx={{ backgroundColor: '#f8fafc' }}>
									<TableCell sx={{ color: '#94a3b8' }}>
										Item & Clinical Requirement
									</TableCell>
									<TableCell
										align="right"
										sx={{ color: '#94a3b8' }}>
										Cost
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{cost.equipment.map(e => (
									<TableRow key={e.item}>
										<TableCell>
											<Typography
												sx={{
													fontWeight: 600,
													fontSize: 14,
												}}>
												{e.item}
											</Typography>
											<HtmlBlock html={e.reason_text} />
										</TableCell>

										<TableCell
											align="right"
											sx={{
												fontWeight: 800,
												fontSize: 14,
											}}>
											{fmt(e.cost)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>

					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							p: 2,
							background: '#f8fafc',
							borderTop: '2px solid #e2e8f0',
						}}>
						<Typography
							sx={{
								fontSize: 12,
								color: '#6b7280',
								fontWeight: 600,
							}}>
							Total Equipment Cost
						</Typography>
						<Typography sx={{ fontSize: 20, fontWeight: 800 }}>
							{fmt(
								cost.equipment.reduce((a, b) => a + b.cost, 0),
							)}
						</Typography>
					</Box>
				</CardContent>
			</Card>
			{data.notes && data.notes !== '' && (
				<Box
					sx={{
						display: 'flex',
						gap: 1,
						alignItems: 'flex-start',
						p: 1,
						border: '1px solid #fde68a',
						backgroundColor: '#fffbeb',
					}}
					className="disclaimer">
					<WarningAmberIcon sx={{ fontSize: 16, flexShrink: 0 }} />

					<Typography sx={{ fontSize: 12, color: '#78350f' }}>
						<strong>Important Notice: </strong>
						{data.notes}
					</Typography>
				</Box>
			)}
			<Box flexDirection={'row'} display={'flex'}>
				<Button
					onClick={onDownload}
					startIcon={<Download />}
					sx={{
						backgroundColor: '#fff',
						color: dark,
						fontSize: '13px',
						fontWeight: 600,
						borderRadius: '8px',
						padding: '8px 16px',
						textTransform: 'none',
						boxShadow: 'none',
						height: '40px',
					}}>
					Download Assessment PDF
				</Button>
				<Button
					onClick={onClickNew}
					startIcon={<Add />}
					sx={{
						backgroundColor: '#fff',
						color: dark,
						fontSize: '13px',
						marginLeft: '10px',
						fontWeight: 600,
						borderRadius: '8px',
						padding: '8px 16px',
						textTransform: 'none',
						boxShadow: 'none',
						height: '40px',
					}}>
					New Assessment
				</Button>
			</Box>
		</Box>
	);
}

function AdmissionAssessment() {
	const homeId = getElderHomeId();
	const userId = getUserId();
	const [activeStep, setActiveStep] = useState(0);
	const [stepOneData, setStepOneData] = useState(initialData);
	const [stepTwoData, setStepTwoData] = useState({});
	const [generatedData, setGeneratedData] = useState({});
	const [fileData, setFileData] = useState([]);
	const [txtError, setTxtError] = useState(INITIAL_ERROR_STATE);
	const [stepOneDataError, setStepOneDataError] = useState({
		name: INITIAL_ERROR_STATE,
		nhs_number: INITIAL_ERROR_STATE,
		date_of_birth: INITIAL_ERROR_STATE,
		date_of_assessment: INITIAL_ERROR_STATE,
		place_of_assessment: INITIAL_ERROR_STATE,
		contact_number: INITIAL_ERROR_STATE,
	});
	const [isLoading, setIsLoading] = useState(false);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [loadingMsgDesc, setLoadingMsgDesc] = useState('');
	const [sessionId, setSessionId] = useState(null);
	const [pdfUrl, setPdfUrl] = useState(null);

	const updateField = (field, value) => {
		setStepOneData(prevData => ({
			...prevData,
			[field]: value,
		}));

		setStepOneDataError(prevData => ({
			...prevData,
			[field]: INITIAL_ERROR_STATE,
		}));
	};

	const updateStepTwoField = (field, value) => {
		setStepTwoData(prevData => ({
			...prevData,
			[field]: value,
		}));
	};

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0] && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData([...fileData, e[0]]);
	}

	function onRemoveFiles(content) {
		const clone = fileData.filter(value => value.name !== content.name);
		setFileData(clone);
	}

	function validateStepOne() {
		let isValid = true;

		const errors = {};

		if (!stepOneData.name) {
			errors.name = {
				status: true,
				msg: 'Enter name',
			};
			isValid = false;
		}

		if (!stepOneData.nhs_number) {
			errors.nhs_number = {
				status: true,
				msg: 'Enter NHS number',
			};
			isValid = false;
		}

		if (!stepOneData.date_of_birth) {
			errors.date_of_birth = {
				status: true,
				msg: 'Enter date of birth',
			};
			isValid = false;
		}

		if (!stepOneData.date_of_assessment) {
			errors.date_of_assessment = {
				status: true,
				msg: 'Enter date of assessment',
			};
			isValid = false;
		}

		if (!stepOneData.place_of_assessment) {
			errors.place_of_assessment = {
				status: true,
				msg: 'Enter place of assessment',
			};
			isValid = false;
		}

		if (!stepOneData.contact_number) {
			errors.contact_number = {
				status: true,
				msg: 'Enter contact number',
			};
			isValid = false;
		}

		if (fileData.length === 0) {
			setTxtError({
				status: true,
				msg: 'Upload files',
			});
			isValid = false;
		}

		if (Object.keys(errors).length > 0) {
			setStepOneDataError(errors);
		}

		return isValid;
	}

	/**
	 * Step 01
	 */
	async function onUploadData() {
		if (!validateStepOne()) return;

		setIsLoading(true);
		setLoadingMsg('Building care profile');
		setLoadingMsgDesc(
			'Extracting conditions, medications, risk factors, and care needs',
		);

		const formData = new FormData();
		fileData.forEach(file => {
			formData.append(
				'documents',
				new Blob([file], { type: 'text/plain' }),
				file.name,
			);
		});
		formData.append('user_id', userId);
		formData.append('client_id', homeId);
		Object.entries(stepOneData).forEach(([key, value]) => {
			formData.append(key, value);
		});

		const responseData = await fetchAdmissionAssessmentData(formData);

		if (!responseData.error) {
			setActiveStep(1);
			setStepTwoData(prevState => ({
				...prevState,
				...responseData.page2,
			}));
			setSessionId(responseData.session_id);
		} else {
			toast.error(responseData.message);
		}

		setIsLoading(false);
		setLoadingMsg('');
		setLoadingMsgDesc('');
	}

	/**
	 * Step 02
	 */
	async function onGenerateData() {
		setIsLoading(true);
		setLoadingMsg('Calculating assessment cost');
		setLoadingMsgDesc('Generating weekly cost estimate');

		const body = {
			user_id: userId,
			client_id: homeId,
			files: '',
			page1: {
				...stepOneData,
			},
			page2: {
				...stepTwoData,
			},
			session_id: sessionId ? sessionId : uuidv4(),
		};

		const responseData = await generateAdmissionAssessment(body);

		if (!responseData.error) {
			setActiveStep(2);
			const data = mapAssessmentResponse(responseData);
			setGeneratedData(data);
		} else {
			toast.error(responseData.message);
		}

		setIsLoading(false);
		setLoadingMsg('');
		setLoadingMsgDesc('');
	}

	function mapAssessmentResponse(api) {
		const configData = api.config_data ?? {};
		const cost = api.cost ?? {};
		setPdfUrl(api.pdf_base64 ?? null);

		return {
			assessment_id: api.assessment_id,
			resident: stepOneData.name ?? '',
			assessed_date: stepOneData.date_of_assessment ?? '',
			config_data: {
				care_type_base_rates: configData?.care_type_base_rates ?? {},
				hourly_care_rate: configData?.hourly_care_rate ?? 0,
			},
			cost: {
				care_type: cost?.care_type ?? '',
				base_weekly_rate_config: cost?.base_weekly_rate_config ?? 0,
				care_hours_per_week_config:
					cost?.care_hours_per_week_config ?? 0,
				hourly_care_rate_config: cost?.hourly_care_rate_config ?? 0,
				dependency_band: cost?.dependency_band ?? '',
				dependency_score: cost?.dependency_score ?? 0,
				care_element: cost?.care_element ?? 0,
				estimated_weekly_total: cost?.estimated_weekly_total ?? 0,
				one_time_total: cost?.one_time_total ?? 0,

				care_type_reason: cost?.care_type_reason ?? '',
				dependency_band_reason: cost?.dependency_band_reason ?? '',

				score_breakdown: cost?.score_breakdown ?? {},
				score_breakdown_reasons_text:
					cost?.score_breakdown_reasons ?? {},

				equipment: (cost?.equipment ?? []).map(item => ({
					item: item.item,
					cost: item.cost,
					reason_text: item.reason,
				})),
			},
			dependency_score: SCORE_ORDER.map(key => {
				const [min, max] =
					configData.dependency_band_thresholds[key] || [];
				const hours = configData.dependency_hours[key];

				return {
					name: key,
					label: `${min}-${max} pts · ${hours ?? '-'} hrs/wk`,
				};
			}),
			notes: cost?.notes ?? '',
		};
	}

	function onReset() {
		setActiveStep(0);
		setStepOneData(initialData);
		setStepTwoData({});
		setGeneratedData({});
		setFileData([]);
		setTxtError(INITIAL_ERROR_STATE);
		setStepOneDataError({
			name: INITIAL_ERROR_STATE,
			nhs_number: INITIAL_ERROR_STATE,
			date_of_birth: INITIAL_ERROR_STATE,
			date_of_assessment: INITIAL_ERROR_STATE,
			place_of_assessment: INITIAL_ERROR_STATE,
			contact_number: INITIAL_ERROR_STATE,
		});
		setIsLoading(false);
		setLoadingMsg('');
		setLoadingMsgDesc('');
		setSessionId(null);
	}

	function onDownloadPdf() {
		if (!pdfUrl) {
			console.error('No PDF data available');
			return;
		}

		const filename = `SenzCare.AI_Pre-Admission_Cost_Estimate_${stepOneData.name}.pdf`;
		const byteCharacters = atob(pdfUrl);
		const byteNumbers = new Array(byteCharacters.length);

		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: 'application/pdf' });

		const url = URL.createObjectURL(blob);

		const link = document.createElement('a');
		link.href = url;
		link.download = filename;
		link.click();

		URL.revokeObjectURL(url);
	}

	return (
		<MainGrid>
			<Box width={'100%'}>
				<Box mb={3}>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="flex-start"
						flexWrap="wrap"
						gap={2}>
						<Box>
							<Typography
								sx={{
									fontSize: '22px',
									fontWeight: 600,
									color: dark,
									mb: 0.5,
								}}>
								Admission Assessment
							</Typography>

							<Typography
								sx={{
									fontSize: 14,
									color: '#6b8888',
								}}>
								Assess a prospective resident prior to admission
								- AI extracts care needs from referral documents
								and calculates estimated weekly cost
							</Typography>
						</Box>
						<Box flexDirection={'row'} display={'flex'}>
							<Button
								onClick={onReset}
								startIcon={<Add />}
								sx={{
									backgroundColor: '#fff',
									color: dark,
									fontSize: '13px',
									marginLeft: '5px',
									fontWeight: 600,
									borderRadius: '8px',
									padding: '8px 16px',
									textTransform: 'none',
									boxShadow: 'none',
									height: '40px',
								}}>
								New Assessment
							</Button>
						</Box>
					</Stack>
				</Box>
				<Box>
					<Box
						sx={{
							display: 'flex',
							mb: 2.5,
							bgcolor: '#fff',
							border: '1px solid #e5e7eb',
							borderRadius: '12px',
							overflow: 'hidden',
						}}>
						{steps.map((step, index) => {
							const active = index === activeStep;

							return (
								<Box
									key={step}
									sx={{
										flex: 1,
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										px: 2,
										py: 1.5,
										cursor: 'pointer',
										opacity: active ? 1 : 0.4,
										bgcolor: active ? '#f8fafc' : '#fff',
										borderRight:
											index !== steps.length - 1
												? '1px solid #e5e7eb'
												: 'none',
									}}>
									<Box
										sx={{
											width: 28,
											height: 28,
											borderRadius: '50%',
											bgcolor: active
												? '#00b7a8'
												: '#94a3b8',
											color: '#fff',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											fontSize: 12,
											fontWeight: 600,
										}}>
										{index + 1}
									</Box>

									<Typography
										sx={{
											fontSize: 13,
											fontWeight: active ? 600 : 500,
										}}>
										{step}
									</Typography>
								</Box>
							);
						})}
					</Box>
					{isLoading ? (
						<Box width={'100%'}>
							<Paper
								elevation={0}
								sx={{
									textAlign: 'center',
									p: '64px 32px',
									borderRadius: '12px',
								}}>
								{/* ICON CIRCLE */}
								<Box
									sx={{
										width: 80,
										height: 80,
										borderRadius: '50%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										margin: '0 auto 20px',
									}}>
									<CircularProgress
										size={60}
										thickness={2}
										sx={{
											color: '#00a7a7',
										}}
									/>
								</Box>

								{/* TITLE */}
								<Typography
									sx={{
										color: '#1f2a2e',
										marginBottom: '10px',
										fontWeight: 700,
										fontSize: '20px',
									}}>
									{loadingMsg}
								</Typography>

								{/* DESCRIPTION */}
								<Typography
									sx={{
										color: '#6b7a80',
										maxWidth: 520,
										margin: '0 auto 28px',
										fontSize: '13.5px',
										lineHeight: 1.6,
									}}>
									{loadingMsgDesc}
								</Typography>
							</Paper>
						</Box>
					) : (
						<>
							{activeStep === 0 && (
								<AdmissionStep1Upload
									data={stepOneData}
									updateField={updateField}
									// onNext={() => setActiveStep(1)}
									readTextFile={readTextFile}
									txtError={txtError}
									fileData={fileData}
									onRemoveFiles={onRemoveFiles}
									handleSubmit={onUploadData}
									errors={stepOneDataError}
								/>
							)}

							{activeStep === 1 && (
								<AdmissionStep2Review
									data={stepTwoData}
									updateField={updateStepTwoField}
									onBack={() => setActiveStep(0)}
									onNext={() => setActiveStep(2)}
									onGenerate={onGenerateData}
								/>
							)}

							{activeStep === 2 && (
								<AdmissionStep3Assessment
									data={generatedData}
									onBack={() => setActiveStep(1)}
									onClickNew={onReset}
									onDownload={onDownloadPdf}
								/>
							)}
						</>
					)}
				</Box>
			</Box>
		</MainGrid>
	);
}

export default AdmissionAssessment;
